import { useCallback, useEffect } from 'react'

import { AnimatePresence, motion } from 'motion/react'

import { Heading, Icon } from '@/components/atoms'
import type { Tailwind as TailwindTypes } from '@/tailwind-types'
import { cn } from '@/utils'

export type TSideDrawerProps = {
  closeOnClickOutside?: boolean
  closeOnEscape?: boolean
  headerClassName?: string
  isOpen: boolean
  maxWidth?: TailwindTypes['maxWidth'] | string
  title?: string
  subtitle?: string
  onClose: () => void
  onExited?: () => void
} & React.PropsWithChildren

export const SideDrawer = ({
  children,
  isOpen,
  closeOnClickOutside = false,
  closeOnEscape = false,
  headerClassName,
  maxWidth = 'max-w-156',
  title,
  subtitle,
  onClose,
  onExited
}: TSideDrawerProps): JSX.Element | null => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && closeOnEscape) {
        onClose()
      }
    },
    [isOpen, onClose, closeOnEscape]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleKeyDown, closeOnEscape])

  return (
    <AnimatePresence onExitComplete={onExited}>
      {isOpen && (
        <motion.div
          key='backdrop'
          className='bg-surface-black-50/75 fixed inset-0 z-50'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={closeOnClickOutside ? onClose : undefined}
          aria-hidden={true}
        >
          <div className='absolute inset-6 z-50'>
            <motion.aside
              key='side-drawer'
              className={cn(
                'bg-surface-white-50 absolute right-0 flex h-full w-full flex-col rounded-2xl p-8 pt-10',
                {
                  [maxWidth]:
                    typeof maxWidth === 'string' &&
                    maxWidth.startsWith('max-w-')
                }
              )}
              style={{
                maxWidth:
                  typeof maxWidth === 'string' && !maxWidth.startsWith('max-w-')
                    ? maxWidth
                    : undefined
              }}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{
                duration: 0.2,
                ease: [0.32, 0.72, 0, 1]
              }}
              onClick={e => e.stopPropagation()}
            >
              <header
                className={cn('mb-4 flex flex-col gap-1', headerClassName)}
              >
                <div className='flex items-center justify-end gap-4 has-[h2]:justify-between'>
                  {title && (
                    <Heading level={'h2'} weight='medium'>
                      {title}
                    </Heading>
                  )}
                  <button
                    aria-label='Close side drawer'
                    className='border-surface-white-300 bg-surface-white-50 hover:bg-surface-white-200 hover:border-surface-white-400 flex size-6 shrink-0 cursor-pointer select-none items-center justify-center rounded-full border transition-colors'
                    type='button'
                    onClick={onClose}
                  >
                    <Icon color='surface-black-50' name='close' />
                  </button>
                </div>
                {subtitle && (
                  <span className='font-body text-body-lg text-surface-body leading-default'>
                    {subtitle}
                  </span>
                )}
              </header>
              <div className='flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
                {children}
              </div>
            </motion.aside>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
